import { Color, Spacing } from '@persona-id/design-tokens';
import {
  CaretDownIcon,
  ImageIcon,
  MoreIcon,
  PlusIcon,
  StarIcon,
  TransferInIcon,
} from '@persona-id/design-tokens/icons';
import { XIcon } from '@persona-id/design-tokens/icons/12px';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { COLORS } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import ReactSelect from 'dashboard/components/ReactSelect';
import FileDisplay, { FileInfo } from 'dashboard/components-lib/FileDisplay/FileDisplay';
import { ModalBody, ModalContainer } from 'dashboard/layouts/ModalLayout';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { List, ListStatus } from 'dashboard/models/list';
import { ListItemFace, ListItemSelfieBackground } from 'dashboard/models/list-item';
import { ProductFeatureName } from 'dashboard/models/organization';
import { RegistryRegistrationData, RegistryRegistrationStatus } from 'dashboard/models/selfie';
import { setLists } from 'dashboard/redux/actions/lists';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu/DropdownMenu';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { Heading, Label } from 'design-system/components/Text';
import IconMaximize from 'images/icons/maximize.svg';
import IconMinimize from 'images/icons/minimize.svg';
import download from 'lib/download';
import ApiResponseError from 'lib/errors/ApiResponseError';
import useApiLoader from 'lib/hooks/useApiLoader';
import useCounter from 'lib/hooks/useCounter';
import { useLeftArrowListener, useRightArrowListener } from 'lib/hooks/useKeyListener';
import useProductFeature from 'lib/hooks/useProductFeature';
import useToggle from 'lib/hooks/useToggle';
import { ApiEndpoint, ApiModel } from 'lib/interfaces/api';
import { isPermissionsError } from 'lib/permissions-errors';
import { getDataFromUrl } from 'lib/utils/requests';

const StyledModalContainer = styled(ModalContainer)`
  width: unset;
`;

const StyledModalBody = styled(ModalBody)`
  max-height: unset;
`;

const FlexButtonGroup = styled('div')`
  display: flex;
  flex: auto;
  flex-direction: row;
  flex-grow: 1;
  justify-content: space-between;
  gap: ${Spacing[2]};
`;

const PhotoWithButtonContainer = styled('div')`
  display: grid;
  grid-column-gap: ${Spacing[4]};
  grid-template-rows: auto auto;
`;

const PhotoButtonContainer = styled('div')`
  position: absolute;
  top: ${Spacing[2]};
  right: ${Spacing[2]};
  z-index: 1;
`;

interface ImageContainerProps {
  width?: string;
  height?: string;
  clickable?: boolean;
}

const ImageContainer = styled('div')<ImageContainerProps>`
  position: relative;
  width: ${({ width }) => width || '150px'};
  min-height: ${({ height }) => height || '200px'};
  cursor: ${({ clickable }) => (clickable ? 'zoom-in' : 'inherit')};
  border-radius: 8px;
  overflow: hidden;
`;

interface ImageProps {
  isLoaded: boolean;
}

const DefaultImage = styled('div')<ImageProps>`
  opacity: ${({ isLoaded }) => (isLoaded ? '0' : '1')};
  transition: opacity 0.3s;
  position: absolute;
  height: 100%;
  width: 100%;
`;

const EmptyImageMessage = styled('div')`
  background: ${({ theme }) => theme.color.background.secondary};
  padding: ${Spacing[2]};
  position: absolute;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const StyledIconImage = styled(ImageIcon)`
  margin-bottom: ${Spacing[2]};

  svg {
    color: ${COLORS.gray50};
  }
`;

const ExtensionStickerContainer = styled('div')`
  position: absolute;
  top: ${Spacing[2]};
  left: ${Spacing[2]};
`;

const IconContainer = styled.div.attrs(() => ({ role: 'button' }))`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 0 ${Spacing[2]};
  height: ${({ theme }) => theme.text.heading.md.lineHeight};

  & svg {
    color: ${({ theme }) => theme.color.text.tertiary};

    path[stroke] {
      stroke: ${({ theme }) => theme.color.text.tertiary};
    }
  }

  &:hover {
    color: ${({ theme }) => theme.color.text.disabled};

    path[stroke] {
      stroke: ${({ theme }) => theme.color.text.disabled};
    }
  }
`;

const Header = styled.header`
  flex: none;
  margin: 0;
  padding: 0 ${Spacing[7]};

  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const StyledIconMaximize = styled(IconMaximize)`
  height: ${Spacing[4]};
  width: ${Spacing[4]};
`;

const StyledIconMinimize = styled(IconMinimize)`
  height: ${Spacing[4]};
  width: ${Spacing[4]};
`;

const ErroredText = styled.div`
  color: ${Color.TextDanger};
`;

const DropdownMenuNonclickableItem = styled.div`
  padding: ${Spacing[2]};
`;

interface AsyncSelfieProps {
  value: FileInfo[];
  placeholder?: JSX.Element;
  width?: string;
  height?: string;
  allowPreviewZoom?: boolean;
  allowModalExpand?: boolean;
  /**
   * When `string`, will be displayed for all images.
   * When `array`, `modalLabel[activeIndex]` will be displayed.
   */
  modalContents?: JSX.Element;
  modalLabel?: string;
  /**
   * Shows e.g. "1 of 5" in the label.
   */
  showCount?: boolean;
  showExtension?: boolean;
  isBeta?: boolean;
  registryRegistrationData?: RegistryRegistrationData;
  showIndexedStatus?: boolean;
  useDropdownUI?: boolean;
  enableSetAsMain?: boolean;
  updateMainSelfie?: () => void;
  sourceToken?: string;
}

const AsyncSelfie = ({
  value,
  placeholder = (
    <EmptyImageMessage>
      <StyledIconImage />
    </EmptyImageMessage>
  ),
  width,
  height,
  allowPreviewZoom = true,
  allowModalExpand,
  modalLabel,
  modalContents,
  showCount,
  showExtension,
  isBeta,
  registryRegistrationData,
  useDropdownUI,
  showIndexedStatus,
  enableSetAsMain,
  updateMainSelfie,
  sourceToken,
}: AsyncSelfieProps) => {
  const featureFlags = useSelector((state: RootState) => state.featureFlags.featureFlags);
  const organization = useSelector((state: RootState) => state.organization.organization);
  const currentEnvironment = useSelector(
    (state: RootState) => state.currentEnvironment.currentEnvironment,
  );
  let lists = useSelector((state: RootState) => state.lists.lists) || [];
  const allowUntrackableSources = useProductFeature(
    ProductFeatureName.ListsFacesUntrackableSources,
  );
  const hasListsEnhanced = useProductFeature(ProductFeatureName.ListsEnhanced);

  const missingDataFromState =
    featureFlags == null || organization == null || currentEnvironment == null;
  const featureListsFacesEnabled = !missingDataFromState && hasListsEnhanced;
  const featureListsSelfieBackgroundEnabled = featureFlags?.['dashboard:selfie-background-list'];

  const [loading, { disable: stopLoading }] = useToggle(true);
  const [hasError, { enable: setError }] = useToggle();
  const [isHover, { enable: setHover, disable: unsetHover }] = useToggle();
  const [isModalOpen, { enable: showModal, disable: hideModal }] = useToggle();
  const [isModalExpanded, { enable: expandModal, disable: shrinkModal }] = useToggle();
  const [activeIndex, { increment, decrement }] = useCounter(0, { mod: value.length });

  const canOpenModal = !loading && !hasError && allowPreviewZoom;
  const canExpandModal = canOpenModal && allowModalExpand;

  const label = useMemo<string>(() => {
    const activeImageLabel = value[activeIndex]?.label ?? modalLabel;
    const countLabel =
      showCount && value.length > 1 ? `${activeIndex + 1} of ${value.length}` : undefined;

    return [activeImageLabel, countLabel].filter(Boolean).join(' - ');
  }, [modalLabel, activeIndex, showCount, value.length]);

  const enableAddToFaceList =
    featureListsFacesEnabled &&
    (allowUntrackableSources || (sourceToken && !sourceToken.startsWith('act')));

  const enableAddToList = enableAddToFaceList || featureListsSelfieBackgroundEnabled;

  const dispatch = useDispatch();

  const params = enableAddToList
    ? {
        filter: {
          'environment-id': currentEnvironment.id,
          status: [ListStatus.Active],
        },
        sort: '-created-at',
      }
    : null;
  useApiLoader({
    loader: async () => {
      const coverageResponse = await SessionClientDashboard.listResource<List>(
        ApiEndpoint.List,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        params,
      );
      lists = coverageResponse.data;
      dispatch(setLists(lists));
    },
    skip: !enableAddToList || (lists != null && lists.length > 0),
    deps: [],
    onError: (error) => {
      if (!isPermissionsError(error)) {
        dispatch(showMessage('Failed to load face lists.', MessageType.Failure));
      }
    },
    onNetworkError: () =>
      dispatch(
        showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
      ),
  });

  const activeLists = lists
    .filter(
      (list) =>
        (list.type === ApiModel.ListFace && enableAddToFaceList) ||
        (list.type === ApiModel.ListSelfieBackground && featureListsSelfieBackgroundEnabled),
    )
    .filter((list) => list.attributes['status'] === ListStatus.Active);
  const activeListsExist = activeLists.length !== 0;
  const defaultListId = activeListsExist ? activeLists[0].id : '';
  const [selectedListId, updateSelectedListId] = useState<string>(defaultListId);

  const faceListOptions =
    enableAddToFaceList && lists.filter((list) => list.type === ApiModel.ListFace);
  const selfieBackgroundListOptions =
    featureListsSelfieBackgroundEnabled &&
    lists.filter((list) => list.type === ApiModel.ListSelfieBackground);

  const options = [];
  if (faceListOptions) {
    options.push({
      label: 'Face Lists',
      options: faceListOptions.map((list) => ({
        label: list.attributes['name'],
        value: list.id,
      })),
    });
  }
  if (selfieBackgroundListOptions) {
    options.push({
      label: 'Selfie Background Lists',
      options: selfieBackgroundListOptions.map((list) => ({
        label: list.attributes['name'],
        value: list.id,
      })),
    });
  }

  const handleAddFaceListItem = async (fileInfo: FileInfo) => {
    try {
      const listId = selectedListId || defaultListId;
      const listType = lists.find((list) => list.id === listId)?.type;
      const data = await getDataFromUrl(fileInfo.url);

      if (listType === ApiModel.ListFace) {
        await SessionClientDashboard.postResourceCreate<ListItemFace>(
          {
            attributes: {
              'list-id': listId,
              'face-photo-upload': data,
              'environment-id': currentEnvironment.id,
              ...(sourceToken ? { 'source-id': sourceToken } : {}),
            },
            type: ApiModel.ListItemFace,
          },
          {
            contentType: 'multipart/form-data',
          },
        );
      } else if (listType === ApiModel.ListSelfieBackground) {
        await SessionClientDashboard.postResourceCreate<ListItemSelfieBackground>(
          {
            attributes: {
              'list-id': listId,
              'selfie-photo-upload': data,
              'environment-id': currentEnvironment.id,
              ...(sourceToken ? { 'source-id': sourceToken } : {}),
            },
            type: ApiModel.ListItemSelfieBackground,
          },
          {
            contentType: 'multipart/form-data',
          },
        );
      } else {
        throw new Error('Invalid list type');
      }
    } catch (error) {
      if (
        error instanceof ApiResponseError &&
        error.status === 409 &&
        error.errors?.[0].details === 'blob already exists as an active item in list'
      ) {
        dispatch(showMessage('Item already exists in this list.', MessageType.Failure));
      } else if (
        error instanceof ApiResponseError &&
        error.status === 403 &&
        typeof error.errors?.[0].details === 'string'
      ) {
        dispatch(showMessage(error.errors?.[0].details, MessageType.Failure));
      } else {
        dispatch(showMessage('Failed to add to the list. Please try again.', MessageType.Failure));
      }
      return;
    }
    dispatch(showMessage('Successfully added to the list.', MessageType.Success));
  };

  if (value.length === 0) {
    return (
      <div>
        <ImageContainer
          width={width}
          height={height}
          clickable={false}
          id='async-selfie__container'
          aria-label='Empty selfie preview'
        >
          <EmptyImageMessage>
            <StyledIconImage /> No image
          </EmptyImageMessage>
        </ImageContainer>
      </div>
    );
  }

  return (
    <div>
      <ImageContainer
        width={width}
        height={height}
        clickable={canOpenModal}
        onMouseEnter={setHover}
        onMouseLeave={unsetHover}
        id='async-selfie__container'
        aria-label='Selfie image'
        // NOTE - if useDropdownUI is true, open modal handler will be attached to the menu item
        onClick={() => !useDropdownUI && canOpenModal && showModal()}
      >
        <DefaultImage isLoaded={!loading}>{placeholder}</DefaultImage>

        {hasError ? (
          <EmptyImageMessage>
            <StyledIconImage /> Failed to load image
          </EmptyImageMessage>
        ) : (
          <PhotoWithButtonContainer>
            <FileDisplay
              data-test='async-selfie__thumbnail'
              file={value[0]}
              loading={loading}
              onFileLoad={stopLoading}
              onError={setError}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              height={parseFloat(height)}
              position='absolute'
              thumbnail
            />
            {useDropdownUI
              ? !loading &&
                (enableAddToFaceList ||
                  enableSetAsMain ||
                  registryRegistrationData?.['error-message']) && (
                  <PhotoButtonContainer>
                    <DropdownMenu
                      side='right'
                      trigger={
                        showIndexedStatus ? (
                          <Button size='sm' suffixIcon={<MoreIcon />}>
                            {registryRegistrationData?.status ===
                            RegistryRegistrationStatus.Succeeded
                              ? 'Indexed'
                              : 'Not indexed'}
                          </Button>
                        ) : (
                          <Button size='sm'>
                            <MoreIcon />
                          </Button>
                        )
                      }
                    >
                      {enableAddToList && (
                        <DropdownMenu.Item
                          id='async-selfie__dropdown-menu__add-to-list'
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            canOpenModal && showModal();
                          }}
                          prefixIcon={<PlusIcon />}
                        >
                          Add to list
                        </DropdownMenu.Item>
                      )}
                      {enableSetAsMain && (
                        <DropdownMenu.Item
                          id='async-selfie__dropdown-menu__item-set-to-as-main'
                          onClick={updateMainSelfie}
                          prefixIcon={<StarIcon />}
                        >
                          Make main selfie
                        </DropdownMenu.Item>
                      )}
                      {registryRegistrationData?.['error-message'] && (
                        <DropdownMenuNonclickableItem>
                          {registryRegistrationData?.['error-message'] && (
                            <ErroredText>{registryRegistrationData['error-message']}</ErroredText>
                          )}
                        </DropdownMenuNonclickableItem>
                      )}
                    </DropdownMenu>
                  </PhotoButtonContainer>
                )
              : isHover &&
                enableAddToList &&
                !loading && (
                  <PhotoButtonContainer>
                    <Button variant='basic' prefixIcon={<PlusIcon />}>
                      Add To List
                    </Button>
                  </PhotoButtonContainer>
                )}
          </PhotoWithButtonContainer>
        )}
        {showExtension && value[0].type != null && (
          <>
            <ExtensionStickerContainer>
              {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
              <Sticker variant='primary'>{value[0].type.split('/').pop().toUpperCase()}</Sticker>
              {isBeta && (
                <div>
                  <Sticker variant='caution'>BETA</Sticker>
                </div>
              )}
            </ExtensionStickerContainer>
          </>
        )}
      </ImageContainer>
      {!hasError && (
        <ModalWrapper isOpen={isModalOpen} handleCloseModal={hideModal}>
          <StyledModalContainer>
            <Header aria-label='Add item to list'>
              <Heading size='md'>{label}</Heading>
              <div style={{ display: 'flex' }}>
                {canExpandModal && !isModalExpanded && (
                  <IconContainer id='modal__expand' onClick={expandModal}>
                    <StyledIconMaximize />
                  </IconContainer>
                )}
                {canExpandModal && isModalExpanded && (
                  <IconContainer id='modal__shrink' onClick={shrinkModal}>
                    <StyledIconMinimize />
                  </IconContainer>
                )}
                <IconContainer id='modal__close' onClick={hideModal}>
                  <XIcon />
                </IconContainer>
              </div>
            </Header>
            <StyledModalBody>
              {modalContents || (
                <AsyncSelfieModal
                  file={value[activeIndex]}
                  placeholder={placeholder}
                  increment={increment}
                  decrement={decrement}
                  multi={value.length > 1}
                  expanded={isModalExpanded}
                />
              )}
              {enableAddToList && (
                <>
                  <Spacer height={3} />
                  <Label
                    color='primary'
                    size='lg'
                    weight='bold'
                    htmlFor='async-selfie-modal__add-to-face-list_select'
                  >
                    Add to list
                  </Label>
                  {activeListsExist && (
                    <ReactSelect
                      id='async-selfie-modal__add-to-list_select'
                      value={selectedListId}
                      options={options}
                      onChange={(id) => updateSelectedListId(id)}
                    />
                  )}
                  <Spacer height={2} />
                  <FlexButtonGroup>
                    {activeListsExist ? (
                      <>
                        <Button
                          id='async-selfie-modal__add-to-face-list__btn__cancel'
                          onClick={hideModal}
                          width='full'
                          variant='basic'
                        >
                          Cancel
                        </Button>
                        <Button
                          id='async-selfie-modal__add-to-face-list__btn__add'
                          onClick={() => handleAddFaceListItem(value[activeIndex])}
                          width='full'
                          variant='primary'
                        >
                          Add to List
                        </Button>
                      </>
                    ) : (
                      <Button
                        id='async-selfie-modal__add-to-face-list__btn__new-list'
                        onClick={() => window.open('/dashboard/lists')}
                        width='full'
                        variant='basic'
                      >
                        Create New List
                      </Button>
                    )}
                  </FlexButtonGroup>
                </>
              )}
            </StyledModalBody>
          </StyledModalContainer>
        </ModalWrapper>
      )}
    </div>
  );
};

export default AsyncSelfie;

interface AsyncSelfieModalBodyProps {
  file: FileInfo;
  placeholder: JSX.Element;
  increment: () => void;
  decrement: () => void;
  multi: boolean;
  expanded: boolean;
}
const AsyncSelfieModal = ({
  file,
  placeholder,
  increment,
  decrement,
  multi,
  expanded,
}: AsyncSelfieModalBodyProps) => {
  const [loading, { enable: startLoading, disable: stopLoading }] = useToggle(true);
  const [hasError, { enable: setError, disable: clearError }] = useToggle();

  const dispatch = useDispatch();

  const handlePrev = useCallback(() => {
    if (!multi) {
      return;
    }
    clearError();
    startLoading();
    decrement();
  }, [multi, decrement]);

  const handleNext = useCallback(() => {
    if (!multi) {
      return;
    }
    clearError();
    startLoading();
    increment();
  }, [multi, increment]);
  useLeftArrowListener(handlePrev);
  useRightArrowListener(handleNext);

  const downloadFile = async () => {
    try {
      await download({
        url: file.url,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        filename: file.name,
      });
    } catch {
      dispatch(showMessage('Failed to download file. Please try again.'));
    }
  };

  const canDownload = !hasError && file.name != null;

  return (
    <ImageContainer
      width={expanded ? '80vw' : '650px'}
      height={expanded ? '70vh' : '480px'}
      aria-label='Selfie preview'
    >
      {multi && (
        <>
          <NavButtonContainer dir='left'>
            <Button
              id='async-selfie-modal__btn-decrement'
              onClick={handlePrev}
              width='icon'
              variant='inverse'
              aria-label='Previous selfie'
            >
              <CaretDownIcon />
            </Button>
          </NavButtonContainer>
          <NavButtonContainer dir='right'>
            <Button
              id='async-selfie-modal__btn-increment'
              onClick={handleNext}
              width='icon'
              variant='inverse'
              aria-label='Next selfie'
            >
              <CaretDownIcon />
            </Button>
          </NavButtonContainer>
        </>
      )}
      {canDownload && (
        <DownloadContainer>
          <Button
            id='async-selfie-modal__btn-download'
            onClick={downloadFile}
            width='icon'
            variant='primary'
            aria-label='Download selfie'
          >
            <TransferInIcon />
          </Button>
        </DownloadContainer>
      )}
      <DefaultImage isLoaded={!loading}>{placeholder}</DefaultImage>
      {hasError ? (
        <EmptyImageMessage>
          <StyledIconImage /> Failed to load image
        </EmptyImageMessage>
      ) : (
        <FileDisplay
          data-test='async-selfie-modal__image'
          file={file}
          loading={loading}
          onFileLoad={stopLoading}
          onError={setError}
          position='absolute'
        />
      )}
    </ImageContainer>
  );
};

const NavButtonContainer = styled.div<{ dir: 'left' | 'right' }>`
  position: absolute;
  height: 100%;
  display: flex;
  align-items: center;
  z-index: ${Z_INDICES.modalElevated};

  ${({ dir }) =>
    dir === 'left'
      ? css`
          left: ${Spacing[4]};
          svg {
            transform: rotate(90deg);
          }
        `
      : css`
          right: ${Spacing[4]};
          svg {
            transform: rotate(270deg);
          }
        `}
`;

const DownloadContainer = styled.div`
  position: absolute;
  z-index: ${Z_INDICES.modalElevated};
  top: ${Spacing[4]};
  right: ${Spacing[4]};
`;
